import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AuthShell from "../components/AuthShell";
import { useAuthActions } from "../context/AuthContext";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 48,
    borderRadius: "12px",
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(79,107,255,.14)" },
  },
};

const roles = [
  "Software Engineer",
  "Frontend Developer",
  "Backend Developer",
  "Data Scientist",
  "Product Manager",
  "DevOps Engineer",
  "UX Designer",
  "Business Analyst",
];

const experienceLevels = ["Student", "Entry level", "Mid level", "Senior", "Leadership"];

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuthActions();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!acceptedTerms) {
      setError("Accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, fullName);
      navigate("/interview-config");
    } catch (err) {
      setError(err?.message || err?.detail || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Create Your Account"
      title="Start practicing interviews with a setup that feels serious."
      subtitle="Tell us what you are preparing for, then begin a realistic interview tailored to your goals."
      formTitle="Create your account"
      formSubtitle="Start free. No credit card required."
      highlights={[
        { title: "Role-specific practice", body: "Choose interviews aligned with the work you want." },
        { title: "Evidence-backed reports", body: "See what worked and where each answer lost clarity." },
        { title: "A personal roadmap", body: "Build the skills that matter most before interview day." },
      ]}
    >
      <Box component="form" onSubmit={handleSubmit}>
        {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Full Name"
            name="name"
            autoComplete="name"
            fullWidth
            required
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            sx={fieldSx}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            fullWidth
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={fieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              label="Role target"
              name="target-role"
              value={targetRole}
              onChange={(event) => setTargetRole(event.target.value)}
              required
              fullWidth
              sx={fieldSx}
            >
              {roles.map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
            </TextField>
            <TextField
              select
              label="Experience level"
              name="experience-level"
              value={experienceLevel}
              onChange={(event) => setExperienceLevel(event.target.value)}
              required
              fullWidth
              sx={fieldSx}
            >
              {experienceLevels.map((level) => <MenuItem key={level} value={level}>{level}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField
            label="Password"
            name="new-password"
            type="password"
            autoComplete="new-password"
            fullWidth
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            helperText="8+ chars, uppercase, lowercase, digit"
            sx={fieldSx}
          />
          <TextField
            label="Confirm Password"
            name="confirm-password"
            type="password"
            autoComplete="new-password"
            fullWidth
            required
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            sx={fieldSx}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                required
                inputProps={{ "aria-label": "Accept Terms of Service and Privacy Policy" }}
                sx={{ color: "#9ca3af", "&.Mui-checked": { color: "#4f6bff" } }}
              />
            )}
            label={<Typography variant="body2" sx={{ color: "#4b5563" }}>I agree to the Terms of Service and Privacy Policy.</Typography>}
            sx={{ alignItems: "flex-start", m: 0 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ minHeight: 48, borderRadius: "12px", bgcolor: "#4f6bff", "&:hover": { bgcolor: "#3e5af5" } }}
          >
            {loading ? <CircularProgress size={20} color="inherit" aria-label="Creating account" /> : "Create Account"}
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" sx={{ textAlign: "center", color: "#6b7280", mt: 3 }}>
        Already have an account?{" "}
        <Typography component={Link} to="/login" variant="body2" sx={{ color: "#3e5af5", fontWeight: 750, textDecoration: "none" }}>
          Sign in
        </Typography>
      </Typography>
    </AuthShell>
  );
}
